/**
 * BlueprintChat — reusable Layer 5 chat surface for any Blueprint page.
 *
 * Renders a quick-button preset row above a Composer + ChatTurn history.
 * Tier-gated chips render in the locked state per Q5 design lock.
 * Calls /api/blueprint-chat which grounds responses in the Blueprint's
 * frozen matchup + scenario_fit snapshots.
 *
 * Hedge phrases in assistant replies are wrapped via the Markdown renderer.
 */

import { useState, useEffect, useRef } from "react";
import ChatTurn from "./ChatTurn.jsx";
import Composer from "./Composer.jsx";
import Chip from "./Chip.jsx";
import MarkdownV2 from "./MarkdownV2.jsx";
import Callout from "./Callout.jsx";
import { canAccess, tierDisplayLabel } from "../lib/feature-gates.js";
import { parseJsonResponse } from "../lib/api-fetch.js";

export default function BlueprintChat({
  blueprintId,
  blueprintType,        // "operating_manual" | "pre_meeting" | "resume" | "relational" | "team"
  presetPrompts = [],   // [{ label, preset_text, tier_required? }]
  bearerToken,
  tier,
  mode = "pre_meeting",
}) {
  const [turns, setTurns] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [turns]);

  // Load existing chat history on mount
  useEffect(() => {
    if (!bearerToken || !blueprintId) return;
    let active = true;
    fetch(`/.netlify/functions/blueprint-chat-history?blueprint_id=${blueprintId}`, {
      headers: { authorization: `Bearer ${bearerToken}` },
    })
      .then((r) => r.json())
      .then((b) => {
        if (active && b.messages) {
          setTurns(b.messages.map((m) => ({
            role: m.role,
            content: m.content,
            ts: new Date(m.created_at),
            hedgePhrases: m.metadata?.hedge_phrases || [],
          })));
        }
      })
      .catch(() => {});
    return () => { active = false; };
  }, [blueprintId, bearerToken]);

  const send = async (text) => {
    if (!text?.trim() || busy) return;
    setBusy(true);
    setError(null);
    setTurns((t) => [...t, { role: "user", content: text, ts: new Date() }]);
    // Auto-retry once on transient 5xx (Netlify cold-start, gateway blip).
    // Mirrors the streaming pre-delta retry pattern from #79. Most chat
    // cold-starts are invisible to the user with this; a sustained
    // failure still surfaces normally on the second attempt.
    const MAX_ATTEMPTS = 2;
    let lastError = null;
    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      try {
        const res = await fetch("/.netlify/functions/blueprint-chat", {
          method: "POST",
          headers: { "content-type": "application/json", authorization: `Bearer ${bearerToken}` },
          body: JSON.stringify({ blueprint_id: blueprintId, user_message: text, mode }),
        });
        // Use parseJsonResponse — surfaces "Server error (HTTP 502) — try
        // again" instead of the SyntaxError "Unexpected token '<', '<HTML>
        // <HE'..." that bare `await res.json()` produces on Netlify
        // error pages. Bug surfaced in chat 2026-05-10; same UX gap that
        // #39 closed for the other API surfaces — chat had been missed.
        const body = await parseJsonResponse(res);
        if (!res.ok) {
          // Auto-retry once on transient 5xx; surface immediately on 4xx
          // (caller error, retry won't fix).
          if (attempt < MAX_ATTEMPTS && res.status >= 500 && res.status < 600) {
            await new Promise((r) => setTimeout(r, 600));
            continue;
          }
          lastError = body.error || `Chat request failed (HTTP ${res.status})`;
          break;
        }
        setTurns((t) => [...t, {
          role: "assistant",
          content: body.content,
          ts: new Date(),
          hedgePhrases: body.hedge_phrases || [],
        }]);
        setBusy(false);
        return;
      } catch (e) {
        // fetch() threw (network unreachable). Auto-retry once.
        if (attempt < MAX_ATTEMPTS) {
          await new Promise((r) => setTimeout(r, 600));
          continue;
        }
        lastError = e.message;
      }
    }
    if (lastError) {
      setError(lastError);
      setTurns((t) => [...t, { role: "assistant", content: `Error: ${lastError}`, ts: new Date() }]);
    }
    setBusy(false);
  };

  return (
    <div data-component="BlueprintChat" style={{ marginTop: 24 }}>
      {presetPrompts.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{
            fontFamily: "var(--ppr-typography-font-family-display)",
            fontSize: "var(--ppr-typography-font-size-sm)",
            letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
            textTransform: "uppercase",
            color: "var(--ppr-color-text-secondary)",
            marginBottom: 10,
          }}>
            Quick-button presets
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {presetPrompts.map((p, i) => {
              const featureKey = p.tier_required ? `${blueprintType}_${p.tier_required}` : null;
              const locked = featureKey && tier && !canAccess(tier, featureKey);
              return (
                <Chip
                  key={i}
                  locked={locked}
                  lockedTierLabel={locked ? tierDisplayLabel(p.tier_required) : undefined}
                  onClick={() => !locked && send(p.preset_text || p.label)}
                >
                  {p.label}
                </Chip>
              );
            })}
          </div>
        </div>
      )}

      {error && (
        <Callout.WatchOut>
          {error}
        </Callout.WatchOut>
      )}

      <div
        ref={scrollRef}
        style={{
          maxHeight: 480,
          overflowY: "auto",
          padding: turns.length ? "16px 4px" : 0,
          marginBottom: turns.length ? 12 : 0,
          background: turns.length ? "var(--ppr-color-background-sub)" : "transparent",
          border: turns.length ? "1px solid var(--ppr-color-border-default)" : "none",
        }}
      >
        {turns.map((t, i) => (
          <ChatTurn key={i} role={t.role} timestamp={t.ts ? t.ts.toLocaleTimeString() : null}>
            {t.role === "assistant"
              ? <MarkdownV2 source={t.content} />
              : t.content}
          </ChatTurn>
        ))}
        {busy && (
          <ChatTurn role="assistant">
            <em style={{ color: "var(--ppr-color-text-muted)" }}>Thinking…</em>
          </ChatTurn>
        )}
      </div>

      <Composer
        placeholder="Ask a follow-up grounded in this Blueprint…"
        onSubmit={send}
        disabled={busy}
      />
    </div>
  );
}
